/**
 * Available colors for DevTools custom track entries
 */
const DEVTOOLS_COLORS = [
  'primary',
  'primary-light',
  'primary-dark',
  'secondary',
  'secondary-light',
  'secondary-dark',
  'tertiary',
  'tertiary-light',
  'tertiary-dark',
  'error'
] as const;

/**
 * Generate a consistent color based on a string hash
 */
const getColorForOperation = (namespace: string): string => {
  let hash = 0;
  for (let i = 0; i < namespace.length; i++) {
    const char = namespace.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  const colorIndex = Math.abs(hash) % DEVTOOLS_COLORS.length;
  const lightColors = ['primary-light', 'secondary-light', 'tertiary-light'];

  // Prefer darker variants for visual distinction
  const selectedColor = DEVTOOLS_COLORS[colorIndex];
  if (lightColors.includes(selectedColor)) {
    const baseColor = selectedColor.replace('-light', '');
    const darkVariant = `${baseColor}-dark`;
    return darkVariant;
  }

  // If not a light color, use as-is (already includes dark variants and regular colors)
  return selectedColor;
};

type PerformanceTracker = {
  start: (label: string) => void;
  end: (label: string) => number;
  measure: <T>(label: string, fn: () => T) => T;
  measureAsync: <T>(label: string, fn: () => Promise<T>) => Promise<T>;
};

/**
 * Creates a performance tracker that creates Chrome DevTools marks
 * but doesn't accumulate measurements in memory to prevent memory leaks.
 * Perfect for performance tracking where we want DevTools visibility
 * without memory overhead.
 *
 * All measurements appear in a custom "Moderne" track in the Performance tab
 * when "Show custom tracks" is enabled in DevTools settings.
 */
export const createPerformanceTracker = (
  namespace: string
): PerformanceTracker => {
  const start = (label: string): void => {
    const startMark = `${namespace}-${label}-start`;

    // Create native performance mark for Chrome DevTools
    try {
      performance.mark(startMark);
    } catch {
      // Silently handle performance API errors
    }
  };

  const end = (label: string): number => {
    const startMark = `${namespace}-${label}-start`;
    const endMark = `${namespace}-${label}-end`;
    const durationMeasure = `${namespace}-${label}-duration`;

    // Create end mark and measure for DevTools
    try {
      performance.mark(endMark);

      // Only create measure if start mark exists
      const existingMarks = performance.getEntriesByName(startMark, 'mark');
      if (existingMarks.length > 0) {
        let measure = performance.measure(durationMeasure, {
          start: startMark,
          end: endMark,
          detail: {
            devtools: {
              dataType: 'track-entry',
              track: 'Moderne',
              color: getColorForOperation(namespace),
              properties: [
                ['Namespace', namespace],
                ['Operation', label],
                ['Type', 'Performance Tracker']
              ],
              tooltipText: `${namespace} - ${label}`
            }
          }
        });

        // Firefox compatibility: measure() may return undefined
        if (!measure) {
          const measures = performance.getEntriesByName(
            durationMeasure,
            'measure'
          );
          measure = measures.at(-1) as PerformanceMeasure;
        }

        const duration = measure?.duration || 0;

        // Clean up marks and measures to prevent memory accumulation
        performance.clearMarks(startMark);
        performance.clearMarks(endMark);
        performance.clearMeasures(durationMeasure);

        return duration;
      }
    } catch {
      // Silently handle performance API errors
    }

    return 0;
  };

  const measure = <T>(label: string, fn: () => T): T => {
    start(label);
    try {
      const result = fn();
      end(label);
      return result;
    } catch (error) {
      end(label);
      throw error;
    }
  };

  const measureAsync = async <T>(
    label: string,
    fn: () => Promise<T>
  ): Promise<T> => {
    start(label);
    try {
      const result = await fn();
      end(label);
      return result;
    } catch (error) {
      end(label);
      throw error;
    }
  };

  return {
    start,
    end,
    measure,
    measureAsync
  };
};
